/** "Send another message" (auto-replies): when a guest replies to a message
 *  this notification produced, the FIRST option whose trigger matches sends
 *  its reply back on the same channel and optionally applies a tag.
 *
 *  Wire (dashboard parity): one `auto_replies` object; options reconcile by
 *  id, `sequence` = list position (stamped on save by the draft), `tag` must
 *  be an object-with-id or null. Expiry sign convention: positive seconds =
 *  stops matching BEFORE tour start, negative = AFTER, null = never expires.
 *  Every option requires a trigger — the page refuses to save otherwise.
 */
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';

import type { AutoReplies, AutoReplyOption, AutoReplyTagRef } from '@/api/notifications';
import * as tagsApi from '@/api/tags';
import { FieldHelp, labelWithHelp } from '@/components/FieldHelp';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { tagChipActions } from '@/components/TagBadgeSet/tagChipActions';

import { AUTO_REPLY_HELP } from './fieldHelp';
import { PickerLinks } from '@/components/ValueActions/PickerLinks';
import { humanizeSeconds } from './targetingSummary';

const EMPTY: AutoReplies = {
  enabled: false,
  timedelta_before_tour_start_to_expire_reply: null,
  mark_answers_as_read: false,
  options: [],
};

let nextClientOptionId = -1;

function newOption(sequence: number): AutoReplyOption {
  return {
    id: nextClientOptionId--,
    sequence,
    answer_trigger: '',
    answer_trigger_is_ai_prompt: false,
    tag: null,
    auto_response: '',
  };
}

/** True while any option is missing its trigger — the save must be refused
 *  (the backend validator rejects it; the dashboard blocks save the same way). */
export function autoRepliesInvalid(value: AutoReplies | null): boolean {
  if (!value) return false;
  return value.options.some((o) => !(o.answer_trigger ?? '').trim());
}

export function autoReplySummary(value: AutoReplies | null): string {
  if (!value || (!value.enabled && value.options.length === 0)) return 'off';
  const parts = [
    value.enabled ? `${value.options.length} repl${value.options.length === 1 ? 'y' : 'ies'}` : 'off',
  ];
  if (value.mark_answers_as_read) parts.push('marks read');
  const td = value.timedelta_before_tour_start_to_expire_reply;
  if (td != null && td !== 0) {
    parts.push(`expires ${humanizeSeconds(td)} ${td > 0 ? 'before' : 'after'} start`);
  }
  return parts.join(' · ');
}

interface Props {
  value: AutoReplies | null;
  onChange: (value: AutoReplies) => void;
  tenantId: number | 'system';
  canManage: boolean;
  /** Option errors keyed by option index ('*' = general). */
  errors?: Record<string, string>;
  /** Set after a refused save so empty triggers show as errors immediately. */
  showValidation?: boolean;
}

export function AutoReplyPanel({ value, onChange, tenantId, canManage, errors, showValidation }: Props) {
  const [expanded, setExpanded] = useState(false);
  const current = value ?? EMPTY;
  const td = current.timedelta_before_tour_start_to_expire_reply;
  const expires = td != null;
  const absTd = Math.abs(td ?? 0);
  const hours = Math.floor(absTd / 3600);
  const minutes = Math.floor((absTd % 3600) / 60);
  const after = (td ?? 0) < 0;

  const patch = (partial: Partial<AutoReplies>) => onChange({ ...current, ...partial });
  const patchOption = (index: number, partial: Partial<AutoReplyOption>) =>
    patch({ options: current.options.map((o, i) => (i === index ? { ...o, ...partial } : o)) });

  const setExpiry = (nextExpires: boolean, nextHours: number, nextMinutes: number, nextAfter: boolean) => {
    const seconds = nextHours * 3600 + nextMinutes * 60;
    patch({
      timedelta_before_tour_start_to_expire_reply: nextExpires ? (nextAfter ? -seconds : seconds) : null,
    });
  };

  const fetchTags = useCallback(
    async (q: string): Promise<AutoReplyTagRef[]> => {
      const res = await tagsApi.list({
        search: q || undefined,
        ...(tenantId !== 'system' ? { tour_operator_id: tenantId } : {}),
        pageSize: 50,
      });
      return res.results.map((t) => ({ id: t.id, name: t.name, category: t.category.code }));
    },
    [tenantId],
  );

  const tagLabel = (tag: AutoReplyTagRef | number | null): string => {
    if (tag == null) return '';
    if (typeof tag === 'number') return `#${tag}`;
    return tag.category ? `${tag.category}: ${tag.name ?? tag.id}` : (tag.name ?? `#${tag.id}`);
  };

  const toggleEnabled = (enabled: boolean) => {
    // Turning on with no options seeds the first row (dashboard parity).
    if (enabled && current.options.length === 0) {
      patch({ enabled, options: [newOption(0)] });
    } else {
      patch({ enabled });
    }
    if (enabled) setExpanded(true);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Accordion
        variant="outlined"
        disableGutters
        expanded={expanded}
        onChange={(_, open) => setExpanded(open)}
        sx={{ '&:before': { display: 'none' }, border: 'none' }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Send another message
            </Typography>
            <FieldHelp help={AUTO_REPLY_HELP.enabled} />
            {!expanded && (
              <Chip
                size="small"
                variant="outlined"
                color={current.enabled ? 'primary' : 'default'}
                label={autoReplySummary(value)}
                sx={{ height: 22, fontSize: 12 }}
              />
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          <Stack spacing={1.5}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={current.enabled}
                  disabled={!canManage}
                  onChange={(e) => toggleEnabled(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  Reply automatically when the guest answers this notification
                </Typography>
              }
            />

            {errors?.['*'] && (
              <Typography variant="caption" color="error">
                {errors['*']}
              </Typography>
            )}

            {current.options.map((option, index) => {
              const triggerEmpty = !(option.answer_trigger ?? '').trim();
              const rowError = errors?.[String(index)];
              return (
                <Box key={option.id ?? `new-${index}`}>
                  {index === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      If the guest replies:
                    </Typography>
                  )}
                  <Box
                    sx={{
                      border: 1,
                      borderColor: rowError || (showValidation && triggerEmpty) ? 'error.main' : 'divider',
                      borderRadius: 1,
                      p: 1.5,
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                      <FormControlLabel
                        sx={{ mr: 0, mt: 0.5 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={option.answer_trigger_is_ai_prompt}
                            disabled={!canManage}
                            onChange={(e) =>
                              patchOption(index, { answer_trigger_is_ai_prompt: e.target.checked })
                            }
                          />
                        }
                        label={labelWithHelp(
                          <Typography variant="caption">Use AI</Typography>,
                          AUTO_REPLY_HELP.aiTrigger,
                        )}
                      />
                      <TextField
                        size="small"
                        multiline
                        maxRows={4}
                        label={option.answer_trigger_is_ai_prompt ? 'AI prompt' : 'Answer'}
                        sx={{ flex: 1, minWidth: 200 }}
                        value={option.answer_trigger}
                        disabled={!canManage}
                        required
                        error={Boolean(rowError) || (showValidation && triggerEmpty)}
                        helperText={
                          rowError ?? (showValidation && triggerEmpty ? 'Required' : undefined)
                        }
                        onChange={(e) => patchOption(index, { answer_trigger: e.target.value })}
                      />
                      <Box sx={{ flex: 1, minWidth: 220, mt: 0.25 }}>
                        <RemoteAutocomplete<AutoReplyTagRef>
                          size="small"
                          label="Apply tag (optional)"
                          queryFn={fetchTags}
                          getOptionLabel={(t) => tagLabel(t)}
                          getOptionKey={(t) => t.id}
                          value={typeof option.tag === 'number' ? { id: option.tag } : option.tag}
                          onChange={(v) => patchOption(index, { tag: (v as AutoReplyTagRef | null) ?? null })}
                          disabled={!canManage}
                          fetchOnEmpty
                        />
                        {option.tag != null && (
                          <PickerLinks
                            items={[
                              {
                                key: typeof option.tag === 'number' ? option.tag : option.tag.id,
                                label: tagLabel(option.tag),
                                actions: tagChipActions(
                                  {
                                    id: typeof option.tag === 'number' ? option.tag : option.tag.id,
                                    name: tagLabel(option.tag),
                                    // The picker carries the category CODE, not
                                    // its id — the category actions live on the
                                    // tag page.
                                    category: null,
                                  },
                                  {
                                    tenantId: tenantId === 'system' ? null : tenantId,
                                    owner: 'booking',
                                  },
                                ),
                              },
                            ]}
                          />
                        )}
                      </Box>
                      <TextField
                        size="small"
                        multiline
                        maxRows={4}
                        label="Reply with (optional)"
                        sx={{ flex: 1, minWidth: 200 }}
                        value={option.auto_response ?? ''}
                        disabled={!canManage}
                        onChange={(e) => patchOption(index, { auto_response: e.target.value })}
                      />
                      <Stack direction="row" sx={{ mt: 0.5 }}>
                        {index < current.options.length - 1 && (
                          <IconButton
                            size="small"
                            aria-label="Move reply down"
                            disabled={!canManage}
                            onClick={() => {
                              const next = [...current.options];
                              const [row] = next.splice(index, 1);
                              next.splice(index + 1, 0, row!);
                              patch({ options: next });
                            }}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        )}
                        {(!current.enabled || current.options.length > 1) && (
                          <IconButton
                            size="small"
                            aria-label="Remove reply option"
                            disabled={!canManage}
                            onClick={() => patch({ options: current.options.filter((_, i) => i !== index) })}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                </Box>
              );
            })}

            <Box>
              <Button
                size="small"
                startIcon={<AddIcon />}
                disabled={!canManage}
                onClick={() => patch({ options: [...current.options, newOption(current.options.length)] })}
              >
                Add a reply
              </Button>
            </Box>

            {current.options.length > 0 && (
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={current.mark_answers_as_read}
                      disabled={!canManage}
                      onChange={(e) => patch({ mark_answers_as_read: e.target.checked })}
                    />
                  }
                  label="Mark conversation as read upon user reply"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={expires}
                      disabled={!canManage}
                      onChange={(e) => setExpiry(e.target.checked, hours || 1, minutes, after)}
                    />
                  }
                  label={labelWithHelp(
                    <Typography variant="body2">Reply expires</Typography>,
                    AUTO_REPLY_HELP.expiry,
                  )}
                />
                {expires && (
                  <>
                    <TextField
                      size="small"
                      type="number"
                      label="Hours"
                      sx={{ width: 100 }}
                      value={hours}
                      disabled={!canManage}
                      inputProps={{ min: 0 }}
                      onChange={(e) =>
                        setExpiry(true, Math.max(0, Number(e.target.value) || 0), minutes, after)
                      }
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="Minutes"
                      sx={{ width: 100 }}
                      value={minutes}
                      disabled={!canManage}
                      inputProps={{ min: 0, max: 59 }}
                      onChange={(e) =>
                        setExpiry(true, hours, Math.max(0, Math.min(59, Number(e.target.value) || 0)), after)
                      }
                    />
                    <RadioGroup
                      row
                      value={after ? 'after' : 'before'}
                      onChange={(e) => setExpiry(true, hours, minutes, e.target.value === 'after')}
                    >
                      <FormControlLabel
                        value="before"
                        control={<Radio size="small" />}
                        disabled={!canManage}
                        label={<Typography variant="body2">before the tour starts</Typography>}
                      />
                      <FormControlLabel
                        value="after"
                        control={<Radio size="small" />}
                        disabled={!canManage || hours === 0 && minutes === 0}
                        label={<Typography variant="body2">after the tour starts</Typography>}
                      />
                    </RadioGroup>
                  </>
                )}
              </Stack>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
}
